import { getSearchHistory } from './../helpers'
import type { GlobalSearchQueryUI, GlobalSearchResultPreview } from '../global-search-models'
import type { PopoverComponent, SearchFieldComponent } from '@getgo/chameleon-web'
import { html, query, state } from 'lit-element'
import { ShellElement } from '../../../common/shell-element'
import { asTranslationKey } from '../../../common/translate-helpers/i18n-utils'
import { getGlobalSearchManager } from '../global-search-manager'
import globalSearch from './global-search-field.styles.scss'
import { nothing } from 'lit-html'
import debounce from 'lodash.debounce'
import type { globalSearchEvents, GlobalSearchQueryEventPayload } from '../namespace'
import { executeSearch, GLOBAL_SEARCH_NAMESPACE, notifySearchFieldQueryChange } from '../namespace'
import { getEventBus } from '../../../services/namespaces/event-bus'

export interface GoToGlobalSearchFieldProps {
  readonly searchResults: readonly GlobalSearchResultPreview[] | undefined
}
export class GoToGlobalSearchField extends ShellElement implements GoToGlobalSearchFieldProps {
  static readonly tagName = 'goto-global-search'

  @state() private searchString = ''
  @state() searchResults: GoToGlobalSearchFieldProps['searchResults'] | undefined
  @state() private searchHistory: readonly GlobalSearchQueryUI[] = []

  @query('chameleon-search-field') private searchField: SearchFieldComponent | undefined
  @query('chameleon-popover') private chameleonPopover: PopoverComponent | undefined

  private placeholder = asTranslationKey('Search')

  private resizeObserver = new ResizeObserver(() => {
    this.updateSearchPopoverWidth()
  })

  static get styles() {
    return globalSearch
  }

  connectedCallback() {
    super.connectedCallback()
    const { resultPageQueryChange } = getEventBus().subscribeTo<
      typeof GLOBAL_SEARCH_NAMESPACE,
      typeof globalSearchEvents
    >(GLOBAL_SEARCH_NAMESPACE)
    resultPageQueryChange.addListener(this.queryListener)
    this.unsubscribeFunctions.push(() => {
      resultPageQueryChange.removeListener(this.queryListener)
    })
  }

  private queryListener = (event: GlobalSearchQueryEventPayload) => {
    this.searchString = event.query.query
    if (this.searchField) {
      this.searchField.value = this.searchString
    }
  }

  firstUpdated() {
    if (this.searchField) {
      this.resizeObserver.observe(this.searchField)

      this.unsubscribeFunctions.push(() => {
        this.resizeObserver.unobserve(this.searchField as Element)
      })
    }
  }

  render() {
    /**
     * When we click on the clear button and focus back on the search field this line will make sure that it doesn't display the previous search results
     * We can't use the change event from the chameleon search field since it's not working properly. Needs to be fixed on chameleon's side https://jira.ops.expertcity.com/browse/CHAMELEON-2001
     */
    this.searchString = this.searchField?.value ?? ''

    const categories = getGlobalSearchManager().getCategories()
    return categories.length
      ? html`
          <chameleon-popover menu z-index="60000">
            <div slot="trigger" class="popover-trigger">
              <chameleon-search-field
                placeholder=${this.placeholder}
                @click=${this.handleKeydownAndClickEvent}
                @keydown=${this.handleKeydownAndClickEvent}
                @keyup=${this.handleKeyupEvent}
                @focus=${this.handleFocus}
              >
              </chameleon-search-field>
            </div>
            <div slot="content">${this.renderPopoverContent()}</div>
          </chameleon-popover>
        `
      : nothing
  }

  private renderPopoverContent() {
    if (this.searchString && this.searchResults) {
      return html`<goto-search-result
        @viewmoreresultsclick=${this.closePopoverAndNavigate}
        searchString=${this.searchString}
        .searchResults=${this.searchResults}
      ></goto-search-result> `
    } else if (this.searchHistory.length) {
      this.chameleonPopover?.open()
      return html` <goto-search-history .searchHistory=${this.searchHistory}></goto-search-history>`
    } else {
      return nothing
    }
  }

  private async setSearchHistory() {
    this.searchHistory = await getSearchHistory()
  }

  private handleFocus() {
    this.setSearchHistory()
    // Force the popover open if a user refocuses on the field and search string is present
    if (this.searchString) {
      this.chameleonPopover?.open()
    }
  }

  private updateSearchPopoverWidth() {
    this.chameleonPopover?.setAttribute('width', Math.max(400, this.searchField?.clientWidth ?? 0).toString())
  }

  private handleKeydownAndClickEvent(event: KeyboardEvent) {
    // Enables us to use the spacebar in the search field and prevents the popover from opening when there is no search query or history
    event.stopPropagation()
  }

  private handleKeyupEvent(event: KeyboardEvent) {
    this.searchString = this.searchField?.value ?? ''
    if (event.key === 'Enter') {
      this.closePopoverAndNavigate()
    } else {
      this.searchDelay()
    }
  }

  private triggerSearch = () => {
    this.closeOrOpenPopover()
    this.updateSearchPopoverWidth()
    if (this.searchString.trim()) {
      const query = { query: this.searchString }
      notifySearchFieldQueryChange({ query })
      getGlobalSearchManager()
        .searchFor(query)
        .then(results => (this.searchResults = getGlobalSearchManager().convertToGlobalSearchResultPreviews(results)))
    }
  }
  private searchDelay = debounce(this.triggerSearch, 300)

  private closeOrOpenPopover() {
    if (this.chameleonPopover) {
      if (this.chameleonPopover.isOpen && !this.searchString) {
        this.chameleonPopover.close()
      } else if (!this.chameleonPopover.isOpen && this.searchString) {
        this.chameleonPopover.open()
      }
    }
  }

  private closePopoverAndNavigate = () => {
    this.chameleonPopover?.close()
    const query = { query: this.searchString }
    executeSearch(query)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [GoToGlobalSearchField.tagName]: GoToGlobalSearchField
  }
}
